import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Mail, Lock, User, Building, Briefcase, AlertCircle, Eye, EyeOff, MapPin, Globe, Instagram, Camera, ChevronDown, Phone, UserPlus, CheckCircle, Shield, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { REGION_OPTIONS, INDUSTRY_OPTIONS } from '../constants/dropdownOptions';
import WordMark from '../components/WordMark';
import SocialAuthButtons from '../components/SocialAuthButtons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Utility function to properly capitalize text
// Converts "JOHN DOE" or "john doe" to "John Doe"
const toTitleCase = (str) => {
  if (!str) return str;
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Capitalize first letter of each sentence for descriptions
const toSentenceCase = (str) => {
  if (!str) return str;
  return str
    .toLowerCase()
    .replace(/(^\s*\w|[.!?]\s*\w)/g, c => c.toUpperCase());
};

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, token: authToken, user: authUser, refreshUser, loading: authLoading } = useAuth();

  // Sprint 13.2: Social-signup mode — user is already authenticated via OAuth;
  // they only need to fill the business fields. Email + name come from OAuth.
  const isSocialMode = searchParams.get('source') === 'social';
  const [socialPrefillLoaded, setSocialPrefillLoaded] = useState(!isSocialMode);

  // Surface OAuth errors that came back from the backend callback
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const oauthError = sp.get('oauth_error');
    if (oauthError) {
      toast.error(oauthError);
    }
  }, []);
  const [step, setStep] = useState(isSocialMode ? 2 : 1); // 1 = Email verification, 2 = Registration form, 3 = Plan selection
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    company: '',
    business_description: '',
    credibility_line: '',
    personal_story: '',
    industry: '',
    region: '',
    website: 'https://',
    instagram: '@',
    linkedin: '',
    profile_photo: '',
    whatsapp_number: '',
    whatsapp_country_code: '+91',
    ref_mi_pin: '',
    invite_code: '', // Invite link tracking code
    referral_code: '', // User/Affiliate referral code
    selected_plan: 'free' // For plan selection step
  });
  const [otherIndustry, setOtherIndustry] = useState('');
  const [otherRegion, setOtherRegion] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [referrer, setReferrer] = useState(null);
  const [inviteSource, setInviteSource] = useState(null); // Track invite link source
  
  // OTP verification states
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [referralInfo, setReferralInfo] = useState(null); // Track referral code info
  const [fieldErrors, setFieldErrors] = useState([]); // Missing fields on submit attempt

  // Clear field errors as user fills in the form
  useEffect(() => {
    if (fieldErrors.length > 0) setFieldErrors([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, otherIndustry, otherRegion]);

  // Form validation - check if all required fields are filled
  const isFormValid = () => {
    const passwordOk = isSocialMode ? true : formData.password.trim() !== '';
    // Photo is optional — profiles with photos get more engagement, but not required
    const hasRequiredFields =
      formData.email.trim() !== '' &&
      passwordOk &&
      formData.name.trim() !== '' &&
      formData.company.trim() !== '' &&
      formData.business_description.trim().length >= 10 &&
      formData.business_description.trim().length <= 300 &&
      formData.industry !== '' &&
      formData.region !== '' &&
      formData.whatsapp_number.length >= 7 &&
      formData.whatsapp_number.length <= 15;

    const hasOtherFieldsIfNeeded =
      (formData.industry !== 'Other' || otherIndustry.trim() !== '') &&
      (formData.region !== 'Other' || otherRegion.trim() !== '');

    return hasRequiredFields && hasOtherFieldsIfNeeded;
  };

  // Returns list of missing required fields with their scroll target IDs
  const getMissingFields = () => {
    const missing = [];
    if (!isSocialMode && !formData.password.trim()) missing.push({ label: 'Password', id: 'field-password' });
    if (!formData.name.trim()) missing.push({ label: 'Full name', id: 'field-name' });
    if (!formData.company.trim()) missing.push({ label: 'Company name', id: 'field-company' });
    if (!formData.business_description.trim() || formData.business_description.trim().length < 10)
      missing.push({ label: 'About my business (min 10 characters)', id: 'field-business' });
    if (!formData.industry) missing.push({ label: 'Industry', id: 'field-industry' });
    if (formData.industry === 'Other' && !otherIndustry.trim())
      missing.push({ label: 'Industry — please specify', id: 'field-industry' });
    if (!formData.region) missing.push({ label: 'Region / City', id: 'field-region' });
    if (formData.region === 'Other' && !otherRegion.trim())
      missing.push({ label: 'Region — please specify', id: 'field-region' });
    if (formData.whatsapp_number.length < 7 || formData.whatsapp_number.length > 15)
      missing.push({ label: 'WhatsApp number (7–15 digits)', id: 'field-whatsapp' });
    return missing;
  };

  // Handle MU-Pin referral from URL
  useEffect(() => {
    const refPin = searchParams.get('ref');
    if (refPin) {
      setFormData(prev => ({ ...prev, ref_mi_pin: refPin.toUpperCase() }));
      // Validate referrer
      validateReferrer(refPin);
    }
  }, [searchParams]);

  // Handle invite link code from URL
  useEffect(() => {
    const inviteCode = searchParams.get('invite');
    if (inviteCode) {
      setFormData(prev => ({ ...prev, invite_code: inviteCode.toUpperCase() }));
      // Validate and get invite source label
      validateInviteCode(inviteCode);
    }
  }, [searchParams]);

  // Handle referral code from URL (ref= param for user referrals REF... or MIU-... or affiliate codes)
  useEffect(() => {
    const refParam = searchParams.get('ref');
    if (refParam) {
      const upperCode = refParam.toUpperCase();
      // REF prefix (production) or MIU- prefix (new format) = user referral
      // Or doesn't match MU-Pin format = could be affiliate code
      if (upperCode.startsWith('REF') || upperCode.startsWith('MIU-') || !upperCode.match(/^[A-Z]{2}\d{4}$/)) {
        validateReferralCode(refParam);
      }
    }
  }, [searchParams]);

  const validateReferralCode = async (code) => {
    try {
      const response = await axios.get(`${API}/referrals/validate/${code}`);
      if (response.data.valid) {
        setFormData(prev => ({ ...prev, referral_code: code.toUpperCase() }));
        setReferralInfo({
          type: response.data.type,
          name: response.data.referrer_name
        });
      }
    } catch (error) {
    }
  };

  const validateInviteCode = async (code) => {
    try {
      const response = await axios.get(`${API}/invite/${code}/validate`);
      if (response.data.valid) {
        setInviteSource(response.data.label || 'Invite Link');
      }
    } catch (error) {
    }
  };

  const validateReferrer = async (muPin) => {
    try {
      const response = await axios.get(`${API}/mu-pin/validate/${muPin}`);
      if (response.data.valid) {
        setReferrer({
          name: response.data.referrer_name,
          company: response.data.referrer_company
        });
      }
    } catch (error) {
    }
  };

  // Sprint 13.2: Social-mode prefill — fetch the OAuth-created user profile
  // and lock in email/name/avatar; user only completes the missing business fields.
  useEffect(() => {
    if (!isSocialMode) return;
    if (authLoading) {
      return;
    }
    if (!authToken && !authUser) {
      // Not logged in but somehow on /register?source=social → kick back to /login
      navigate('/login', { replace: true });
      return;
    }
    (async () => {
      try {
        const resp = await axios.get(`${API}/profile`, {
          headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
          withCredentials: true,
          timeout: 10000,
        });
        const u = resp.data || {};

        // Read LinkedIn/Google prefill stored by SocialDone.js
        let headline = '';
        let avatarFromPrefill = '';
        try {
          const prefillRaw = sessionStorage.getItem('linkedin_prefill');
          if (prefillRaw) {
            const prefill = JSON.parse(prefillRaw);
            headline = prefill.headline || '';
            avatarFromPrefill = prefill.avatar || '';
            sessionStorage.removeItem('linkedin_prefill'); // consume once
          }
        } catch (_) {}

        const resolvedPhoto = u.profile_photo || avatarFromPrefill || '';
        setFormData((prev) => ({
          ...prev,
          email: u.email || '',
          name: u.name || '',
          profile_photo: resolvedPhoto,
          linkedin: u.linkedin || prev.linkedin || '',
          // Pre-fill credibility_line from LinkedIn headline (editable)
          credibility_line: headline || prev.credibility_line || '',
          // Sentinel so isFormValid() passes; never sent to the social endpoint
          password: '__oauth__',
        }));
        if (resolvedPhoto) setPhotoPreview(resolvedPhoto);
        setOtpVerified(true);    // Email is already "verified" via OAuth
        setSocialPrefillLoaded(true);
      } catch (err) {
        toast.error('Could not load your social profile. Please try signing in again.');
        navigate('/login', { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSocialMode, authToken, authUser, authLoading]);

  // Cooldown timer for resending OTP
  useEffect(() => {
    if (cooldown > 0) {
      const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldown]);

  // Send OTP to email
  const handleSendOtp = async () => {
    if (!formData.email.trim() || !formData.email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setSendingOtp(true);
    setError('');

    try {
      await axios.post(`${API}/auth/send-verification-otp`, {
        email: formData.email.toLowerCase()
      }, { withCredentials: true });
      setOtpSent(true);
      setCooldown(60);
      toast.success('Verification code sent to your email!');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to send verification code';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSendingOtp(false);
    }
  };

  // Handle OTP input change
  const handleOtpChange = (index, value) => {
    if (value.length > 1) return; // Only single digit
    if (value && !/^\d$/.test(value)) return; // Only numbers

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  // Handle OTP paste
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      setOtp(pastedData.split(''));
    }
  };

  // Handle backspace in OTP input
  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  // Verify OTP
  const handleVerifyOtp = async () => {
    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    setVerifyingOtp(true);
    setError('');

    try {
      await axios.post(`${API}/auth/verify-email-otp`, {
        email: formData.email.toLowerCase(),
        otp: otpCode
      }, { withCredentials: true });
      setOtpVerified(true);
      setStep(2);
      toast.success('Email verified successfully!');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Invalid verification code';
      setError(errorMsg);
      toast.error(errorMsg);
      setOtp(['', '', '', '', '', '']);
    } finally {
      setVerifyingOtp(false);
    }
  };

  // Handle photo upload - DO NOT manipulate images that are already correct
  // Modern browsers handle EXIF orientation in <img> tags automatically
  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Show local preview immediately
    const localUrl = URL.createObjectURL(file);
    setPhotoPreview(localUrl);
    setError('');
    // Upload to Cloudinary via backend (server-side compression + storage)
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await axios.post(`${API}/feed/upload-image`, fd, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
      });
      const cloudUrl = resp.data?.url || resp.data?.secure_url || '';
      if (cloudUrl) {
        setFormData(prev => ({ ...prev, profile_photo: cloudUrl }));
        setPhotoPreview(cloudUrl);
      }
    } catch (err) {
      // Fallback: keep local base64 if Cloudinary fails
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        setFormData(prev => ({ ...prev, profile_photo: base64 }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const missing = getMissingFields();
    if (missing.length > 0) {
      setFieldErrors(missing);
      // Scroll to the first missing field
      const firstEl = document.getElementById(missing[0].id);
      if (firstEl) firstEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setFieldErrors([]);
    // Proceed with registration
    await completeRegistration();
  };

  const completeRegistration = async () => {
    setLoading(true);
    setError('');

    // Use custom values if "Other" is selected; combine WhatsApp country code + number
    const submitData = {
      ...formData,
      industry: formData.industry === 'Other' ? otherIndustry.trim() : formData.industry,
      region: formData.region === 'Other' ? otherRegion.trim() : formData.region,
      whatsapp_number: `${formData.whatsapp_country_code}${formData.whatsapp_number}`,
    };

    try {
      // Sprint 13.2: Social-mode signup updates the existing OAuth user instead of creating new
      if (isSocialMode) {
        await axios.post(
          `${API}/auth/complete-social-registration`,
          {
            company: submitData.company,
            business_description: submitData.business_description,
            industry: submitData.industry,
            region: submitData.region,
            whatsapp_number: submitData.whatsapp_number,
            website: submitData.website,
            instagram: submitData.instagram,
            linkedin: submitData.linkedin,
            credibility_line: submitData.credibility_line,
            personal_story: submitData.personal_story,
            profile_photo: submitData.profile_photo,
            ref_mi_pin: submitData.ref_mi_pin,
            invite_code: submitData.invite_code,
            referral_code: submitData.referral_code,
          },
          {
            withCredentials: true,
            headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
          }
        );
        toast.success('Profile saved — one last step!');
        if (typeof refreshUser === 'function') {
          await refreshUser();
        }
        await new Promise((resolve) => setTimeout(resolve, 200));
        navigate('/dashboard', { replace: true });
        return;
      }

      const result = await register(submitData);
      toast.success('Account created successfully!');
      
      // Small delay to ensure auth state is fully updated
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Check if there's a pending subscription from pricing page
      const pendingSubscription = localStorage.getItem('pendingSubscription');
      if (pendingSubscription) {
        navigate('/pricing', { replace: true });
      } else {
        // Go directly to dashboard - profile completion is triggered when user tries Browse/Match
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      console.error('Registration error:', err);
      console.error('Error response:', err.response?.data);
      const errorMessage = err.response?.data?.detail || err.response?.data?.error?.message || err.message || 'Registration failed. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage, { duration: 5000 });
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF7] flex items-center justify-center px-4 py-12 font-mono text-[#0F172A]">
      <Helmet>
        <title>Join Match It Up — Start Networking With Intent</title>
        <meta name="description" content="Create your Match It Up account. AI-powered professional networking that finds you real business matches — not just connections." />
        <meta property="og:title" content="Join Match It Up — Professional Networking" />
        <meta property="og:description" content="Sign up for Match It Up. AI-powered matchmaking for founders, investors, and professionals." />
      </Helmet>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <button 
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 mb-6 hover:opacity-80 transition-opacity"
          >
            <WordMark size="md" />
          </button>
          <h1 className="text-4xl md:text-5xl font-black uppercase tracking-tight text-[#0F172A] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Join Match It Up©
          </h1>
          <p className="text-base md:text-lg leading-relaxed text-[#0F172A] font-bold">
            Start networking with your business community
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-xl shadow-slate-200/60 p-8">
          {/* Referrer Banner (MU-Pin) */}
          {referrer && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-4"
              data-testid="referrer-banner"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center">
                  <UserPlus className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium text-indigo-800">
                    Invited by <span className="font-bold">{referrer.name}</span>
                  </p>
                  {referrer.company && (
                    <p className="text-xs text-indigo-600">{referrer.company}</p>
                  )}
                </div>
                <CheckCircle className="w-5 h-5 text-green-500 ml-auto" />
              </div>
              <p className="text-xs text-indigo-600 mt-2">
                Complete registration to automatically send a connection request!
              </p>
            </motion.div>
          )}

          {/* Referral Code Banner */}
          {referralInfo && !referrer && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4"
              data-testid="referral-banner"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full flex items-center justify-center">
                  <UserPlus className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    Referred by <span className="font-bold">{referralInfo.name}</span>
                  </p>
                  <p className="text-xs text-amber-600">
                    {referralInfo.type === 'user' ? 'Match It Up Member' : 'Partner'}
                  </p>
                </div>
                <CheckCircle className="w-5 h-5 text-green-500 ml-auto" />
              </div>
            </motion.div>
          )}

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Step 1: Email Verification */}
          {step === 1 && (
            <div className="space-y-5">
              {/* Social auth — Google + LinkedIn */}
              <SocialAuthButtons />

              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-slate-200" />
                <span className="text-xs text-slate-400 font-mono uppercase tracking-wider">or use email</span>
                <div className="flex-1 h-px bg-slate-200" />
              </div>

              {/* Progress indicator */}
              <div className="flex items-center justify-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-full bg-[#E8430A] text-white flex items-center justify-center text-sm font-bold">1</div>
                <div className="w-16 h-1 bg-slate-200 rounded"></div>
                <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-400 flex items-center justify-center text-sm font-bold">2</div>
              </div>
              
              <div className="text-center mb-4">
                <Shield className="w-12 h-12 text-[#E8430A] mx-auto mb-3" />
                <h2 className="text-lg font-semibold text-slate-800">Verify Your Email</h2>
              </div>

              {/* Email Input */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({ ...formData, email: e.target.value.toLowerCase().trim() });
                      setOtpSent(false);
                      setOtp(['', '', '', '', '', '']);
                      setError('');
                    }}
                    disabled={otpSent && !otpVerified}
                    className={`w-full bg-white border border-slate-200 focus:border-[#E8430A] focus:ring-0 rounded-xl py-3 pl-12 pr-4 transition-all ${otpSent ? 'bg-slate-100' : ''}`}
                    placeholder="your@email.com"
                    data-testid="register-email-input"
                    autoComplete="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck="false"
                  />
                  {otpSent && !otpVerified && (
                    <button
                      type="button"
                      onClick={() => {
                        setOtpSent(false);
                        setOtp(['', '', '', '', '', '']);
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#E8430A] hover:text-[#D13A08] font-medium"
                    >
                      Change
                    </button>
                  )}
                </div>
              </div>

              {/* Send OTP Button */}
              {!otpSent && (
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={sendingOtp || !formData.email.includes('@')}
                  className={`w-full py-3 transition-all flex items-center justify-center gap-2 ${
                    sendingOtp || !formData.email.includes('@')
                      ? 'bg-slate-200 text-slate-400 border border-slate-200 rounded-xl cursor-not-allowed font-black uppercase tracking-tight'
                      : 'bg-[#E8430A] text-white border border-slate-200 rounded-xl font-black uppercase tracking-tight shadow-xl shadow-slate-200/50 hover:shadow-[3px_3px_0px_0px_#E8430A] hover:-translate-y-0.5 hover:-translate-x-0.5'
                  }`}
                  data-testid="send-otp-btn"
                >
                  {sendingOtp ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Verification Code
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              )}

              {/* OTP Input */}
              {otpSent && !otpVerified && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2 text-center">
                      Enter 6-digit code sent to {formData.email}
                    </label>
                    <div className="flex justify-center gap-2" onPaste={handleOtpPaste}>
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          id={`otp-${index}`}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleOtpChange(index, e.target.value)}
                          onKeyDown={(e) => handleOtpKeyDown(index, e)}
                          className="w-11 h-12 text-center text-xl font-bold border-2 border-slate-200 rounded-lg focus:border-[#E8430A] focus:ring-[#E8430A] transition-all"
                          data-testid={`otp-input-${index}`}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Verify Button */}
                  <button
                    type="button"
                    onClick={handleVerifyOtp}
                    disabled={verifyingOtp || otp.join('').length !== 6}
                    className={`w-full py-3 transition-all flex items-center justify-center gap-2 ${
                      verifyingOtp || otp.join('').length !== 6
                        ? 'bg-slate-200 text-slate-400 border border-slate-200 rounded-xl cursor-not-allowed font-black uppercase tracking-tight'
                        : 'bg-[#E8430A] text-white border border-slate-200 rounded-xl font-black uppercase tracking-tight shadow-xl shadow-slate-200/50 hover:shadow-[3px_3px_0px_0px_#E8430A] hover:-translate-y-0.5 hover:-translate-x-0.5'
                    }`}
                    data-testid="verify-otp-btn"
                  >
                    {verifyingOtp ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Verify & Continue
                      </>
                    )}
                  </button>

                  {/* Resend OTP */}
                  <div className="text-center">
                    <p className="text-sm text-slate-500">
                      Didn&apos;t receive the code?{' '}
                      {cooldown > 0 ? (
                        <span className="text-slate-400">Resend in {cooldown}s</span>
                      ) : (
                        <button
                          type="button"
                          onClick={handleSendOtp}
                          disabled={sendingOtp}
                          className="text-[#E8430A] hover:text-[#D13A08] font-medium"
                        >
                          Resend Code
                        </button>
                      )}
                    </p>
                  </div>
                </motion.div>
              )}

              <p className="text-center text-slate-600 pt-4 border-t border-slate-100">
                Already have an account?{' '}
                <Link to="/login" className="text-[#E8430A] hover:text-[#D13A08] font-medium">
                  Login
                </Link>
              </p>
            </div>
          )}

          {/* Step 2: Registration Form */}
          {step === 2 && (
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Progress indicator */}
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center text-sm">
                <CheckCircle className="w-4 h-4" />
              </div>
              <div className="w-16 h-1 bg-green-500 rounded"></div>
              <div className="w-8 h-8 rounded-full bg-[#E8430A] text-white flex items-center justify-center text-sm font-bold">2</div>
            </div>

            {/* Error Display for Step 2 */}
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Social-mode welcome banner */}
            {isSocialMode && socialPrefillLoaded && (
              <div
                className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-2"
                data-testid="social-register-banner"
              >
                <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-1" />
                <p className="text-xs text-blue-700 leading-relaxed">
                  Signed in as <strong>{formData.name || formData.email}</strong>. Tell us about your business so we can find your matches.
                </p>
              </div>
            )}

            {/* Verified email badge */}
            <div className="flex items-center justify-center gap-2 bg-green-50 border border-green-200 rounded-lg py-2 px-3 mb-4">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-700">{formData.email}</span>
              <span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded-full">Verified</span>
            </div>

            {/* Profile Photo */}
            <div id="field-photo" className="flex flex-col items-center mb-2">
              <div className="relative">
                <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-slate-100 bg-slate-50 flex items-center justify-center">
                  {photoPreview ? (
                    <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-10 h-10 text-slate-300" />
                  )}
                </div>
                <label className="absolute bottom-0 right-0 w-8 h-8 bg-[#D13A08] rounded-full flex items-center justify-center cursor-pointer hover:bg-[#B83207] transition-colors">
                  <Camera className="w-4 h-4 text-white" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    className="hidden"
                  />
                </label>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                {isSocialMode ? 'Photo from your social account — change if you like' : 'Add a photo (optional — gets 3× more engagement)'}
              </p>
            </div>

            {/* Password — hidden in social mode (user already authenticated via OAuth) */}
            {!isSocialMode && (
            <>
            <div id="field-password">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full bg-slate-50 border-slate-200 focus:border-[#E8430A] focus:ring-[#E8430A] rounded-xl py-3 pl-12 pr-12 transition-all"
                  placeholder="Create a password"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            {/* Social signup alternative */}
            <div>
              <div className="flex items-center gap-3 my-2">
                <div className="flex-1 h-px bg-[#0F172A]/10" />
                <span className="text-xs font-bold text-[#0F172A]/40 uppercase tracking-wider">or sign up with</span>
                <div className="flex-1 h-px bg-[#0F172A]/10" />
              </div>
              <SocialAuthButtons data-testid="social-auth-buttons" />
            </div>
            </>
            )}

            {/* Name */}
            <div id="field-name">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Name
              </label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  onBlur={(e) => setFormData({ ...formData, name: toTitleCase(e.target.value) })}
                  className="w-full bg-slate-50 border-slate-200 focus:border-[#E8430A] focus:ring-[#E8430A] rounded-xl py-3 pl-12 pr-4 transition-all"
                  placeholder="Your full name"
                  data-testid="register-name-input"
                />
              </div>
            </div>

            {/* Company */}
            <div id="field-company">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Company
              </label>
              <div className="relative">
                <Building className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  required
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  onBlur={(e) => setFormData({ ...formData, company: toTitleCase(e.target.value) })}
                  className="w-full bg-slate-50 border-slate-200 focus:border-[#E8430A] focus:ring-[#E8430A] rounded-xl py-3 pl-12 pr-4 transition-all"
                  placeholder="Your company name"
                  data-testid="register-company-input"
                />
              </div>
            </div>

            {/* About My Business */}
            <div id="field-business">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                About My Business
              </label>
              <div className="relative">
                <textarea
                  required
                  value={formData.business_description}
                  onChange={(e) => {
                    if (e.target.value.length <= 300) {
                      setFormData({ ...formData, business_description: e.target.value });
                    }
                  }}
                  onBlur={(e) => setFormData({ ...formData, business_description: toSentenceCase(e.target.value) })}
                  rows={3}
                  maxLength={300}
                  className="w-full bg-slate-50 border-slate-200 focus:border-[#E8430A] focus:ring-[#E8430A] rounded-xl py-3 px-4 transition-all resize-none"
                  placeholder="What does your business do? Who do you serve? What makes you unique?"
                  data-testid="register-business-description-input"
                />
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-slate-400">
                    Helps us find relevant connections for you
                  </span>
                  <span className={`text-xs ${formData.business_description.length > 250 ? 'text-amber-500' : 'text-slate-400'}`}>
                    {formData.business_description.length}/300
                  </span>
                </div>
              </div>
            </div>

            {/* Credibility Line */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Credibility in one line <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.credibility_line}
                  onChange={(e) => {
                    if (e.target.value.length <= 80) {
                      setFormData({ ...formData, credibility_line: e.target.value });
                    }
                  }}
                  onBlur={(e) => setFormData({ ...formData, credibility_line: toSentenceCase(e.target.value) })}
                  maxLength={80}
                  className="w-full bg-slate-50 border-slate-200 focus:border-[#E8430A] focus:ring-[#E8430A] rounded-xl py-3 px-4 transition-all"
                  placeholder="e.g. 3x founder · Ex-Zomato · ₹10Cr ARR"
                  data-testid="register-credibility-line-input"
                />
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-slate-400">
                    Keep it sharp — this is what connectors read before deciding to introduce you
                  </span>
                  <span className={`text-xs ${formData.credibility_line.length > 60 ? 'text-amber-500' : 'text-slate-400'}`}>
                    {formData.credibility_line.length}/80
                  </span>
                </div>
              </div>
            </div>

            {/* Personal Story */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Your story <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <textarea
                value={formData.personal_story}
                onChange={(e) => {
                  if (e.target.value.length <= 300) {
                    setFormData({ ...formData, personal_story: e.target.value });
                  }
                }}
                maxLength={300}
                rows={3}
                className="w-full bg-slate-50 border border-slate-200 focus:border-[#E8430A] focus:ring-[#E8430A] rounded-xl py-3 px-4 transition-all resize-none"
                placeholder="Share a defining moment in your career, why you started your company, or a core value you hold..."
                data-testid="register-personal-story-input"
              />
              <div className="flex justify-between mt-1">
                <span className="text-xs text-slate-400">The AI reads this to find deeper value-alignment when matching you</span>
                <span className={`text-xs ${formData.personal_story.length > 240 ? 'text-red-500' : formData.personal_story.length > 200 ? 'text-amber-500' : 'text-slate-400'}`}>
                  {formData.personal_story.length}/300
                </span>
              </div>
            </div>

            {/* Industry */}
            <div id="field-industry">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Industry
              </label>
              <div className="relative">
                <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                <select
                  required
                  value={formData.industry}
                  onChange={(e) => {
                    setFormData({ ...formData, industry: e.target.value });
                    if (e.target.value !== 'Other') setOtherIndustry('');
                  }}
                  className="w-full bg-slate-50 border-slate-200 focus:border-[#E8430A] focus:ring-[#E8430A] rounded-xl py-3 pl-12 pr-10 transition-all appearance-none cursor-pointer"
                  data-testid="register-industry-input"
                >
                  <option value="">Select your industry</option>
                  {INDUSTRY_OPTIONS.map((industry) => (
                    <option key={industry} value={industry}>{industry}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              </div>
              {formData.industry === 'Other' && (
                <input
                  type="text"
                  required
                  value={otherIndustry}
                  onChange={(e) => setOtherIndustry(e.target.value)}
                  className="w-full mt-2 bg-slate-50 border-slate-200 focus:border-[#E8430A] focus:ring-[#E8430A] rounded-xl py-3 px-4 transition-all"
                  placeholder="Please specify your industry"
                  data-testid="register-other-industry-input"
                />
              )}
            </div>

            {/* Region */}
            <div id="field-region">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Region
              </label>
              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                <select
                  required
                  value={formData.region}
                  onChange={(e) => {
                    setFormData({ ...formData, region: e.target.value });
                    if (e.target.value !== 'Other') setOtherRegion('');
                  }}
                  className="w-full bg-slate-50 border-slate-200 focus:border-[#E8430A] focus:ring-[#E8430A] rounded-xl py-3 pl-12 pr-10 transition-all appearance-none cursor-pointer"
                  data-testid="register-region-input"
                >
                  <option value="">Select your region</option>
                  {REGION_OPTIONS.map((region) => (
                    <option key={region} value={region}>{region}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              </div>
              {formData.region === 'Other' && (
                <input
                  type="text"
                  required
                  value={otherRegion}
                  onChange={(e) => setOtherRegion(e.target.value)}
                  className="w-full mt-2 bg-slate-50 border-slate-200 focus:border-[#E8430A] focus:ring-[#E8430A] rounded-xl py-3 px-4 transition-all"
                  placeholder="Please specify your region"
                  data-testid="register-other-region-input"
                />
              )}
              <p className="mt-1 text-xs text-slate-500">Your city or business region for local networking</p>
            </div>

            {/* Website */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Website <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <div className="relative">
                <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="w-full bg-slate-50 border-slate-200 focus:border-[#E8430A] focus:ring-[#E8430A] rounded-xl py-3 pl-12 pr-4 transition-all"
                  placeholder="https://yourwebsite.com"
                  data-testid="register-website-input"
                />
              </div>
            </div>

            {/* Instagram */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Instagram <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <div className="relative">
                <Instagram className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text"
                  value={formData.instagram}
                  onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                  className="w-full bg-slate-50 border-slate-200 focus:border-[#E8430A] focus:ring-[#E8430A] rounded-xl py-3 pl-12 pr-4 transition-all"
                  placeholder="@yourusername"
                  data-testid="register-instagram-input"
                />
              </div>
            </div>

            {/* LinkedIn (Optional) */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                LinkedIn Profile <span className="text-slate-400 font-normal">(Optional)</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg className="w-5 h-5 text-[#0A66C2]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 10.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </div>
                <input
                  type="text"
                  value={formData.linkedin}
                  onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                  className="w-full bg-slate-50 border-slate-200 focus:border-[#0A66C2] focus:ring-[#0A66C2] rounded-xl py-3 pl-12 pr-4 transition-all"
                  placeholder="linkedin.com/in/yourprofile"
                  data-testid="register-linkedin-input"
                />
              </div>
            </div>

            {/* WhatsApp Number */}
            <div id="field-whatsapp">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                WhatsApp Number
              </label>
              <div className="relative flex">
                <div className="flex items-center bg-slate-100 rounded-l-full border border-r-0 border-slate-200">
                  <Phone className="w-4 h-4 text-green-600 ml-3 mr-1 flex-shrink-0" />
                  <select
                    value={formData.whatsapp_country_code}
                    onChange={(e) => setFormData({ ...formData, whatsapp_country_code: e.target.value })}
                    className="bg-transparent text-slate-700 font-medium text-sm pr-2 pl-1 py-3 focus:outline-none cursor-pointer"
                    data-testid="register-whatsapp-country-code"
                  >
                    <option value="+91">🇮🇳 +91</option>
                    <option value="+1">🇺🇸 +1</option>
                    <option value="+44">🇬🇧 +44</option>
                    <option value="+61">🇦🇺 +61</option>
                    <option value="+65">🇸🇬 +65</option>
                    <option value="+971">🇦🇪 +971</option>
                    <option value="+60">🇲🇾 +60</option>
                    <option value="+62">🇮🇩 +62</option>
                    <option value="+66">🇹🇭 +66</option>
                    <option value="+84">🇻🇳 +84</option>
                    <option value="+49">🇩🇪 +49</option>
                    <option value="+33">🇫🇷 +33</option>
                    <option value="+39">🇮🇹 +39</option>
                    <option value="+34">🇪🇸 +34</option>
                    <option value="+31">🇳🇱 +31</option>
                    <option value="+46">🇸🇪 +46</option>
                    <option value="+47">🇳🇴 +47</option>
                    <option value="+45">🇩🇰 +45</option>
                    <option value="+41">🇨🇭 +41</option>
                    <option value="+81">🇯🇵 +81</option>
                    <option value="+82">🇰🇷 +82</option>
                    <option value="+86">🇨🇳 +86</option>
                    <option value="+55">🇧🇷 +55</option>
                    <option value="+52">🇲🇽 +52</option>
                    <option value="+27">🇿🇦 +27</option>
                    <option value="+234">🇳🇬 +234</option>
                    <option value="+254">🇰🇪 +254</option>
                    <option value="+92">🇵🇰 +92</option>
                    <option value="+880">🇧🇩 +880</option>
                    <option value="+94">🇱🇰 +94</option>
                    <option value="+977">🇳🇵 +977</option>
                    <option value="+974">🇶🇦 +974</option>
                    <option value="+966">🇸🇦 +966</option>
                    <option value="+972">🇮🇱 +972</option>
                    <option value="+90">🇹🇷 +90</option>
                  </select>
                </div>
                <input
                  type="tel"
                  required
                  value={formData.whatsapp_number}
                  onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value.replace(/\D/g, '').slice(0, 15) })}
                  className="flex-1 pl-4 pr-4 py-3 border border-slate-200 rounded-r-full focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="Mobile number"
                  maxLength={15}
                  data-testid="register-whatsapp-input"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1 ml-1">Get instant match & message notifications on WhatsApp</p>
            </div>

            {/* Missing fields error block */}
            {fieldErrors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  Please complete these fields before continuing:
                </p>
                <ul className="space-y-1 pl-6">
                  {fieldErrors.map((f) => (
                    <li
                      key={f.id + f.label}
                      className="text-sm text-red-600 list-disc cursor-pointer hover:text-red-800 hover:underline"
                      onClick={() => {
                        const el = document.getElementById(f.id);
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }}
                    >
                      {f.label}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-[#E8430A] text-white rounded-xl px-8 py-4 font-black uppercase tracking-tight border border-slate-200 shadow-xl shadow-slate-200/50 hover:shadow-[3px_3px_0px_0px_#E8430A] hover:-translate-y-0.5 hover:-translate-x-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:translate-x-0 flex items-center justify-center gap-2 ${
                !isFormValid() ? 'opacity-50' : ''
              }`}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto" />
              ) : !isFormValid() ? (
                'READY TO MATCH'
              ) : (
                'Create Account'
              )}
            </button>
            
            {/* Clickwrap - Terms & Privacy */}
            <p className="text-center text-xs text-slate-400 mt-3">
              By signing up, you agree to our{' '}
              <Link to="/terms" className="text-slate-500 hover:text-[#E8430A] underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-slate-500 hover:text-[#E8430A] underline">Privacy Policy</Link>
            </p>

            <p className="text-center text-slate-600">
              Already have an account?{' '}
              <Link to="/login" className="text-[#E8430A] hover:text-[#D13A08] font-medium">
                Login
              </Link>
            </p>
          </form>
          )}

        </div>
      </motion.div>
    </div>
  );
};

export default Register;
